import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle, ArrowRight, User, Phone, Mail, MapPin, Briefcase,
  UploadCloud, FileText, Image as ImageIcon, X, Loader2, AlertCircle,
  Sparkles, Circle, ListChecks, ShieldCheck,
  Scale, HelpCircle, TrendingUp, DollarSign, Gauge,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirror of backend ALLOWED_EXTENSIONS (server.py) minus anything video. No videos for now.
const MAX_FILES = 5;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB — matches backend MAX_FILE_SIZE
const ALLOWED_EXT = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".gif", ".txt", ".xlsx", ".xls", ".csv"];
const ACCEPT = ALLOWED_EXT.join(",");
const IMAGE_EXT = [".jpg", ".jpeg", ".png", ".gif"];

function extOf(name = "") {
  const i = name.lastIndexOf(".");
  return i === -1 ? "" : name.slice(i).toLowerCase();
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Lightweight one-shot confetti — no dependencies. Renders a burst of colored pieces that fall + fade.
function Confetti({ count = 44 }) {
  const colors = ["#34d399", "#60a5fa", "#a78bfa", "#fbbf24", "#f472b6", "#22d3ee"];
  const pieces = Array.from({ length: count }, (_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 0.25;
    const dur = 2.2 + Math.random() * 1.4;
    const size = 6 + Math.random() * 6;
    const color = colors[i % colors.length];
    const rot = Math.random() * 360;
    const drift = (Math.random() - 0.5) * 120;
    return (
      <span key={i} style={{
        position: "absolute", top: "-16px", left: `${left}%`, width: size, height: size * 0.5,
        background: color, opacity: 0.9, borderRadius: 1, transform: `rotate(${rot}deg)`,
        animation: `confettiFall ${dur}s ${delay}s cubic-bezier(.25,.6,.4,1) forwards`,
        // pass per-piece horizontal drift to the keyframe via CSS var
        ["--drift"]: `${drift}px`,
      }} />
    );
  });
  return (
    <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 50 }}>
      <style>{`@keyframes confettiFall{0%{transform:translate(0,0) rotate(0)}100%{transform:translate(var(--drift),105vh) rotate(540deg);opacity:0}}`}</style>
      {pieces}
    </div>
  );
}

// Count a number up from 0 to target once `start` is true.
function useCountUp(target, start, duration = 900) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start || target == null) return;
    let raf; const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, start, duration]);
  return val;
}

export default function IntakeSuccess() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const leadData = state?.leadData || {};
  const sessionId = state?.sessionId || null;
  const firstName = (leadData.name || "").trim().split(/\s+/)[0] || "";

  // attachments: { id, name, size, ext, file, status: 'staged'|'uploading'|'done'|'error', error?, docId? }
  // Files are STAGED on pick and only sent when the user taps Submit. 'error' = rejected on pick (bad type/size).
  const [attachments, setAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [scoreTab, setScoreTab] = useState("meter"); // "meter" | "score"
  const fileInputRef = useRef(null);
  const idCounter = useRef(0);

  // Celebrate the submission once, on arrival.
  useEffect(() => {
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 4200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // ~60s at 3s interval
    let timer = null;

    const fetchReadiness = async () => {
      try {
        const r = await axios.get(`${API}/intake/${sessionId}/readiness`);
        if (cancelled) return;
        setReadiness(r.data);
        // Keep polling while the LLM case score is still being generated in the background.
        if (r.data?.score_kind === "preliminary" && attempts < MAX_ATTEMPTS) {
          attempts += 1;
          timer = setTimeout(fetchReadiness, 3000);
        }
      } catch (_) {
        if (cancelled) return;
        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          timer = setTimeout(fetchReadiness, 3000);
        }
      }
    };

    fetchReadiness();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  const scoreGenerating = readiness?.score_kind === "preliminary";

  const score = readiness?.score ?? readiness?.readiness_score ?? 0;
  const countScore = useCountUp(score, !!readiness);

  const fields = [
    { icon: User, label: "Name", value: leadData.name },
    { icon: Phone, label: "Phone", value: leadData.phone },
    { icon: Mail, label: "Email", value: leadData.email },
    { icon: MapPin, label: "Location", value: leadData.location },
    { icon: Briefcase, label: "Category", value: leadData.case_category },
  ].filter(f => f.value);

  // Anything that occupies one of the 5 slots (rejected items don't count).
  const slotCount = attachments.filter(a => a.status !== "error").length;
  const remaining = MAX_FILES - slotCount;
  const canUpload = Boolean(sessionId) && remaining > 0 && !submitting;

  const stagedCount = attachments.filter(a => a.status === "staged").length;
  const doneCount = attachments.filter(a => a.status === "done").length;
  // "Received" banner only once everything that occupies a slot is saved — no staged, uploading, or errored items left.
  const allReceived = doneCount > 0 && attachments.every(a => a.status === "done");

  async function uploadOne(att) {
    setAttachments(prev => prev.map(a => a.id === att.id ? { ...a, status: "uploading", error: undefined } : a));
    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("file", att.file);
      const res = await axios.post(`${API}/chat/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments(prev => prev.map(a => a.id === att.id ? { ...a, status: "done", docId: res.data.doc_id } : a));
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      setAttachments(prev => prev.map(a => a.id === att.id ? { ...a, status: "error", error: msg } : a));
    }
  }

  async function submitAll() {
    // Snapshot the files needing upload (staged, or a previous error the user kept its file for retry).
    const pending = attachments.filter(a => a.status === "staged" && a.file);
    if (!sessionId || pending.length === 0) return;
    setSubmitting(true);
    await Promise.all(pending.map(uploadOne));
    setSubmitting(false);
  }

  function handleFiles(fileList) {
    if (!sessionId || submitting) return;
    const files = Array.from(fileList || []);
    let slots = remaining;

    for (const file of files) {
      if (slots <= 0) break;
      const ext = extOf(file.name);
      const localId = ++idCounter.current;

      if (!ALLOWED_EXT.includes(ext)) {
        setAttachments(prev => [...prev, { id: localId, name: file.name, size: file.size, ext, status: "error", error: "Unsupported file type" }]);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setAttachments(prev => [...prev, { id: localId, name: file.name, size: file.size, ext, status: "error", error: "File is larger than 20MB" }]);
        continue;
      }

      slots -= 1;
      setAttachments(prev => [...prev, { id: localId, name: file.name, size: file.size, ext, file, status: "staged" }]);
    }
  }

  function onInputChange(e) {
    handleFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file after removal
  }

  function onDrop(e) {
    e.preventDefault();
    setDragActive(false);
    if (!canUpload) return;
    handleFiles(e.dataTransfer.files);
  }

  function removeAttachment(localId) {
    if (submitting) return;
    setAttachments(prev => prev.filter(a => a.id !== localId));
  }

  return (
    <div className="relative bg-background flex items-center justify-center px-4 overflow-hidden" style={{ minHeight: '100dvh', paddingTop: 'env(safe-area-inset-top,0px)', paddingBottom: 'env(safe-area-inset-bottom,0px)' }} data-testid="success-page">
      {showConfetti && <Confetti />}
      {/* soft ambient glow behind the hero */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(52,211,153,0.18), rgba(96,165,250,0.10) 50%, transparent 70%)" }} />

      <div className="relative max-w-lg w-full text-center space-y-5 sm:space-y-6 py-8">
        {/* Hero check with pulsing rings */}
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto" style={{ animation: "scorePop .6s cubic-bezier(.34,1.56,.64,1) both" }}>
          <style>{`@keyframes scorePop{0%{transform:scale(.4);opacity:0}60%{transform:scale(1.12)}100%{transform:scale(1);opacity:1}}@keyframes ringPulse{0%{transform:scale(1);opacity:.5}100%{transform:scale(1.8);opacity:0}}`}</style>
          <span aria-hidden="true" className="absolute inset-0 rounded-full bg-emerald-400/20" style={{ animation: "ringPulse 2.2s ease-out infinite" }} />
          <span aria-hidden="true" className="absolute inset-0 rounded-full bg-emerald-400/20" style={{ animation: "ringPulse 2.2s ease-out infinite 1.1s" }} />
          <div className="relative w-full h-full rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-400" />
          </div>
        </div>

        <div className="space-y-2 sm:space-y-3 animate-fade-in-up">
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="success-title">
            {firstName ? `Nice work, ${firstName}!` : "Nice work — you're all set!"}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed px-2">
            Your intake is complete and on its way to the right attorney. Here's how you did — and what happens next.
          </p>
        </div>

        {/* 1 ▸ Here's what we're doing right now — the call comes FIRST, above everything */}
        <div className="p-4 sm:p-5 rounded-xl border border-primary/20 bg-primary/5 text-left animate-fade-in-up" data-testid="what-happens-next">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="w-4 h-4 text-primary" />
            <span className="font-semibold text-sm text-foreground">Here's what we're doing right now</span>
          </div>
          <div className="space-y-3 mb-4">
            <div className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-primary/20 border border-primary/30 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
              <div>
                <p className="text-xs sm:text-sm font-medium text-foreground">We'll call you within <strong className="text-primary">24 hours</strong></p>
                <p className="text-xs text-muted-foreground mt-0.5">One of our team members will give you a quick call to check in and make sure everything looks right before your case goes to an attorney.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-primary/20 border border-primary/30 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
              <div>
                <p className="text-xs sm:text-sm font-medium text-foreground">You'll hear from an attorney's office within <strong className="text-primary">48 hours</strong></p>
                <p className="text-xs text-muted-foreground mt-0.5">We'll connect your case to an attorney's office in your area that handles situations like yours, and they'll reach out to you directly about your case.</p>
              </div>
            </div>
          </div>
          <div className="space-y-2 pt-3 border-t border-primary/10">
            {[
              "Keep your phone nearby — we'll call within 24 hours",
              "If you miss our call, check your voicemail — we'll leave instructions",
              "An attorney's office will follow up within 48 hours about your case",
            ].map((tip, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground/80">
                <CheckCircle className="w-3.5 h-3.5 text-primary/60 flex-shrink-0 mt-0.5" />
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 2 ▸ Confidence Meter — two tabs: meter overview + full Case Score. NOT advice, NOT a prediction of outcome. */}
        {readiness && (
          <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] text-left overflow-hidden animate-fade-in-up" data-testid="readiness-card">
            <style>{`@keyframes scorePop{0%{transform:scale(.4);opacity:0}60%{transform:scale(1.12)}100%{transform:scale(1);opacity:1}}`}</style>

            {/* Tab switcher */}
            <div className="flex items-center gap-1 p-1.5 border-b border-border/40 bg-background/30">
              <button type="button" onClick={() => setScoreTab("meter")} data-testid="tab-meter"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 transition-all ${scoreTab === "meter" ? "bg-primary/15 text-primary border border-primary/30" : "text-muted-foreground hover:text-foreground border border-transparent"}`}>
                <Sparkles className="w-3.5 h-3.5" /> Confidence Meter
              </button>
              <button type="button" onClick={() => setScoreTab("score")} data-testid="tab-score"
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-1.5 transition-all ${scoreTab === "score" ? "bg-primary/15 text-primary border border-primary/30" : "text-muted-foreground hover:text-foreground border border-transparent"}`}>
                <ListChecks className="w-3.5 h-3.5" /> Case Score
              </button>
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 border border-border/50 rounded-full px-2 py-0.5">
                <ShieldCheck className="w-3 h-3" /> Estimate only
              </span>
            </div>

            <div className="p-4 sm:p-6">
              {scoreGenerating && (
                <div
                  data-testid="score-generating-banner"
                  className="mb-4 flex items-start gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5"
                  role="status"
                  aria-live="polite"
                >
                  <div className="w-4 h-4 mt-0.5 border-2 border-primary/70 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  <div className="flex-1 text-[12px] leading-relaxed text-foreground/90">
                    <span className="font-semibold">Generating your case score…</span>{" "}
                    <span className="text-muted-foreground">Please wait a few seconds. If it doesn't appear shortly, refresh this page.</span>
                  </div>
                </div>
              )}
              {scoreTab === "meter" ? (
                <>
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <h3 className="text-sm font-semibold">{readiness.title || "Your Confidence Meter"}</h3>
                  </div>

                  {/* Score — animated radial ring that fills as the number counts up */}
                  <div className="flex items-center gap-4 mb-3">
                    {(() => {
                      const R = 34, CIRC = 2 * Math.PI * R;
                      const ringColor = score >= 80 ? "#34d399" : score >= 50 ? "#fbbf24" : "#38bdf8";
                      const textColor = score >= 80 ? "text-emerald-400" : score >= 50 ? "text-amber-400" : "text-sky-400";
                      const offset = CIRC * (1 - countScore / 100);
                      return (
                        <div className="relative w-24 h-24 flex-shrink-0" style={{ animation: "scorePop .6s cubic-bezier(.34,1.56,.64,1) both" }}>
                          <svg width="96" height="96" viewBox="0 0 80 80" className="-rotate-90">
                            <circle cx="40" cy="40" r={R} fill="none" stroke="currentColor" strokeWidth="6" className="text-muted-foreground/15" />
                            <circle cx="40" cy="40" r={R} fill="none" stroke={ringColor} strokeWidth="6" strokeLinecap="round"
                              strokeDasharray={CIRC} strokeDashoffset={offset} style={{ filter: `drop-shadow(0 0 5px ${ringColor}66)` }} />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className={`text-2xl font-bold leading-none ${textColor}`}>{countScore}</span>
                            <span className="text-[9px] text-muted-foreground mt-0.5">/ 100</span>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="flex-1">
                      {readiness.band_label && (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 mb-1.5 border ${
                          score >= 80 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                            : score >= 50 ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                            : "bg-sky-500/10 text-sky-400 border-sky-500/20"}`}>
                          <CheckCircle className="w-3 h-3" /> {readiness.band_label}
                        </span>
                      )}
                      <p className="text-sm text-foreground/90 leading-relaxed">{readiness.headline}</p>
                    </div>
                  </div>

                  {/* Encouragement — the satisfying, confidence-building line */}
                  {readiness.encouragement && (
                    <p className="text-xs text-foreground/80 leading-relaxed mb-3 bg-primary/[0.06] border border-primary/15 rounded-lg px-3 py-2">
                      🎉 {readiness.encouragement}
                    </p>
                  )}

                  {/* How the score works (transparency + that it's adjustable) */}
                  {readiness.how_it_works && (
                    <p className="text-[11px] text-muted-foreground leading-relaxed italic">{readiness.how_it_works}</p>
                  )}

                  <button type="button" onClick={() => setScoreTab("score")} data-testid="see-case-score"
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:gap-1.5 transition-all">
                    See your full Case Score <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <>
                  {/* Case Score detail — the full picture behind the case (NO Win % ever) */}
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <ListChecks className="w-4 h-4 text-primary" />
                    <h3 className="text-sm font-semibold">Your Case Score</h3>
                    <span className="ml-auto text-sm font-bold text-foreground">{countScore}<span className="text-[10px] text-muted-foreground font-normal"> / 100</span></span>
                  </div>

                  {leadData.case_category && (
                    <div className="flex items-center gap-2 text-xs mb-3">
                      <Briefcase className="w-3.5 h-3.5 text-primary/60 flex-shrink-0" />
                      <span className="text-foreground/80 font-medium">{leadData.case_category}</span>
                    </div>
                  )}

                  {readiness.rationale && (
                    <p className="text-xs text-foreground/80 leading-relaxed mb-4">{readiness.rationale}</p>
                  )}

                  {/* Case strength — qualitative chips only (validity / evidence / potential value). NEVER a win %. */}
                  {(() => {
                    const cstr = readiness.case_strength || {};
                    const chips = [];
                    if (typeof cstr.validity === "number") chips.push({ label: "Claim validity", value: `${cstr.validity}/100` });
                    if (typeof cstr.evidence_strength === "number") chips.push({ label: "Evidence strength", value: `${cstr.evidence_strength}/100` });
                    if (cstr.potential_value) chips.push({ label: "Potential value (est.)", value: cstr.potential_value });
                    if (cstr.time_sensitivity) chips.push({ label: "Time sensitivity", value: cstr.time_sensitivity });
                    if (chips.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {chips.map((c, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5 text-[11px] rounded-lg border border-border/50 bg-background/40 px-2.5 py-1">
                            <Gauge className="w-3 h-3 text-primary/70" />
                            <span className="text-muted-foreground">{c.label}:</span>
                            <span className="font-semibold text-foreground/90">{c.value}</span>
                          </span>
                        ))}
                      </div>
                    );
                  })()}

                  {/* The single biggest strengthener */}
                  {readiness.strength_summary && (
                    <p className="text-xs text-foreground/85 leading-relaxed mb-4 bg-primary/[0.06] border border-primary/15 rounded-lg px-3 py-2">
                      <strong className="text-primary">Biggest opportunity: </strong>{readiness.strength_summary}
                    </p>
                  )}

                  {/* 🟢 Positive factors — what supports the case */}
                  {(readiness.positive_factors?.length > 0 || readiness.why_positive?.length > 0) && (
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] p-3.5 mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                        <p className="text-xs font-semibold text-emerald-300">Positive factors — what's working in your favor</p>
                      </div>
                      <ul className="space-y-1.5">
                        {(readiness.positive_factors?.length ? readiness.positive_factors : readiness.why_positive).map((g, i) => (
                          <li key={i} className="text-xs text-foreground/85 flex gap-2">
                            <span className="text-emerald-400 mt-0.5 flex-shrink-0">✓</span><span>{g}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 🔴 Negative factors — what holds the case back */}
                  {(readiness.negative_factors?.length > 0 || readiness.why_negative?.length > 0) && (
                    <div className="rounded-xl border border-red-500/20 bg-red-500/[0.05] p-3.5 mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <AlertCircle className="w-3.5 h-3.5 text-red-400" />
                        <p className="text-xs font-semibold text-red-300">Negative factors — what could hold it back</p>
                      </div>
                      <ul className="space-y-1.5">
                        {(readiness.negative_factors?.length ? readiness.negative_factors : readiness.why_negative).map((r, i) => (
                          <li key={i} className="text-xs text-foreground/85 flex gap-2">
                            <span className="text-red-400 mt-0.5 flex-shrink-0">!</span><span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* ⚖️ Laws that may apply */}
                  {readiness.applicable_laws?.length > 0 && (
                    <div className="rounded-xl border border-border/40 bg-card/40 p-3.5 mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Scale className="w-3.5 h-3.5 text-primary" />
                        <p className="text-xs font-semibold">Laws that may apply to your case</p>
                      </div>
                      <ul className="space-y-2">
                        {readiness.applicable_laws.map((law, i) => (
                          <li key={i} className="text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-foreground/90">{law.code || "Legal standard"}</span>
                              {law.relevance && (
                                <span className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${
                                  /high/i.test(law.relevance) ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                                    : /low/i.test(law.relevance) ? "bg-muted/40 text-muted-foreground border-border/50"
                                    : "bg-amber-500/10 text-amber-400 border-amber-500/20"}`}>{law.relevance}</span>
                              )}
                            </div>
                            {law.description && <p className="text-muted-foreground mt-0.5 leading-relaxed">{law.description}</p>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 📈 What would strengthen your case most (strengtheners — NOT a win promise) */}
                  {readiness.strengtheners?.length > 0 && (
                    <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.05] p-3.5 mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <TrendingUp className="w-3.5 h-3.5 text-sky-400" />
                        <p className="text-xs font-semibold text-sky-300">What would strengthen your case most</p>
                      </div>
                      <ul className="space-y-1.5">
                        {readiness.strengtheners.map((w, i) => (
                          <li key={i} className="text-xs text-foreground/85 flex gap-2">
                            <span className="text-sky-400 mt-0.5 flex-shrink-0">↑</span><span>{w}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 💰 What could raise your case's value */}
                  {readiness.value_drivers?.length > 0 && (
                    <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.05] p-3.5 mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <DollarSign className="w-3.5 h-3.5 text-violet-400" />
                        <p className="text-xs font-semibold text-violet-300">What could increase your case's value</p>
                      </div>
                      <ul className="space-y-1.5">
                        {readiness.value_drivers.map((v, i) => (
                          <li key={i} className="text-xs text-foreground/85 flex gap-2">
                            <span className="text-violet-400 mt-0.5 flex-shrink-0">+</span><span>{v}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* ❓ Questions that still need answering */}
                  {readiness.questions?.length > 0 && (
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] p-3.5 mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <HelpCircle className="w-3.5 h-3.5 text-amber-400" />
                        <p className="text-xs font-semibold text-amber-300">Questions that still need answering</p>
                      </div>
                      <ul className="space-y-1.5">
                        {readiness.questions.map((q, i) => (
                          <li key={i} className="text-xs text-foreground/85 flex gap-2">
                            <span className="text-amber-400 mt-0.5 flex-shrink-0">?</span><span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 📎 Missing information / evidence to gather */}
                  {readiness.missing_info?.length > 0 && (
                    <div className="rounded-xl border border-border/40 bg-card/40 p-3.5 mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <FileText className="w-3.5 h-3.5 text-primary" />
                        <p className="text-xs font-semibold">Missing information &amp; evidence to gather</p>
                      </div>
                      <ul className="space-y-1.5">
                        {readiness.missing_info.map((m, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex gap-2">
                            <span className="text-primary mt-0.5 flex-shrink-0">→</span><span>{m}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Fallback while the AI analysis is still computing — show the aspects we looked at */}
                  {!(readiness.positive_factors?.length) && !(readiness.negative_factors?.length)
                    && !(readiness.why_positive?.length) && !(readiness.why_negative?.length)
                    && !(readiness.applicable_laws?.length) && !(readiness.strengtheners?.length)
                    && readiness.aspects?.length > 0 && (
                    <div className="rounded-xl border border-border/40 bg-card/40 p-3.5 mb-3">
                      <p className="text-[11px] text-muted-foreground mb-2 italic">Your full case analysis is being prepared. Here's what we've reviewed so far:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                        {readiness.aspects.map((a) => (
                          <div key={a.label} className="flex items-center gap-2 text-xs">
                            {a.present
                              ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                              : <Circle className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" />}
                            <span className={a.present ? "text-foreground/90" : "text-muted-foreground"}>{a.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* How to raise your score — supporting documents emphasized */}
                  {readiness.improve?.length > 0 && (
                    <div className="rounded-xl border border-primary/25 bg-primary/[0.06] p-3.5">
                      <div className="flex items-center gap-1.5 mb-2">
                        <UploadCloud className="w-3.5 h-3.5 text-primary" />
                        <p className="text-xs font-semibold">How to raise your Case Score</p>
                      </div>
                      <ul className="space-y-1.5">
                        {readiness.improve.map((t, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex gap-2">
                            <span className="text-primary mt-0.5 flex-shrink-0">→</span><span>{t}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {/* What's next — part of the meter card, sits right above Please read */}
              {readiness.whats_next && (
                <div className="flex items-start gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] p-3.5 mt-4" data-testid="whats-next">
                  <ArrowRight className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-foreground/85 leading-relaxed">{readiness.whats_next}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Evidence upload — attaches to the lead in the back office */}
        {sessionId && (
          <div className="p-4 sm:p-6 rounded-2xl border border-border/50 bg-card/30 text-left space-y-4 animate-fade-in-up" data-testid="evidence-upload">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Add supporting evidence</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Optional. Documents or photos that help your case — up to {MAX_FILES} files, 20MB each. No videos.
                </p>
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0">{slotCount}/{MAX_FILES}</span>
            </div>

            <button
              type="button"
              onClick={() => canUpload && fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (canUpload) setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              disabled={!canUpload}
              data-testid="evidence-dropzone"
              className={`w-full rounded-xl border border-dashed p-5 flex flex-col items-center justify-center gap-2 transition-all ${
                canUpload
                  ? `cursor-pointer ${dragActive ? "border-primary bg-primary/10" : "border-border/60 hover:border-primary/50 hover:bg-secondary/20"}`
                  : "border-border/30 opacity-50 cursor-not-allowed"
              }`}
            >
              <UploadCloud className="w-6 h-6 text-primary/70" />
              <span className="text-sm font-medium text-foreground">
                {remaining > 0 ? "Tap to upload or drag files here" : "Upload limit reached"}
              </span>
              <span className="text-[11px] text-muted-foreground">PDF, Word, Excel, images, text</span>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              onChange={onInputChange}
              className="hidden"
              data-testid="evidence-input"
            />

            {attachments.length > 0 && (
              <ul className="space-y-2" data-testid="evidence-list">
                {attachments.map((a) => {
                  const isImage = IMAGE_EXT.includes(a.ext);
                  const Icon = isImage ? ImageIcon : FileText;
                  return (
                    <li key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border/40 bg-background/40">
                      <Icon className="w-4 h-4 text-primary/60 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-foreground truncate">{a.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {a.status === "error" ? (
                            <span className="text-red-400">{a.error}</span>
                          ) : (
                            formatSize(a.size)
                          )}
                        </p>
                      </div>
                      {a.status === "staged" && <span className="text-[10px] font-medium text-amber-400/90 flex-shrink-0">Ready</span>}
                      {a.status === "uploading" && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin flex-shrink-0" />}
                      {a.status === "done" && <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                      {a.status === "error" && <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                      {a.status !== "done" && (
                        <button
                          type="button"
                          onClick={() => removeAttachment(a.id)}
                          disabled={submitting}
                          className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 disabled:opacity-30"
                          aria-label="Remove file"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Submit — nothing is sent until this is tapped */}
            {stagedCount > 0 && (
              <button
                type="button"
                onClick={submitAll}
                disabled={submitting}
                data-testid="evidence-submit-btn"
                className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 active:scale-[0.98]"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                  : <>Submit {stagedCount} {stagedCount === 1 ? "attachment" : "attachments"}</>}
              </button>
            )}

            {/* Confirmation — only once every file is saved */}
            {allReceived && !submitting && (
              <div className="flex items-start gap-2.5 p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10" data-testid="evidence-received">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-emerald-300/90 leading-relaxed">
                  <strong className="text-emerald-300">Your evidence has been received</strong> and attached to your case — it's safe to leave this page.
                </p>
              </div>
            )}
          </div>
        )}

        {/* 4 ▸ Please read — disclaimers, standalone at the very bottom */}
        {readiness && (readiness.disclaimers?.length > 0 || readiness.disclaimer) && (
          <div className="rounded-xl border border-border/40 bg-background/40 p-3.5 text-left animate-fade-in-up" data-testid="disclaimers">
            <div className="flex items-center gap-1.5 mb-2">
              <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Please read</p>
            </div>
            <ul className="space-y-1.5">
              {(readiness.disclaimers || [readiness.disclaimer]).filter(Boolean).map((d, i) => (
                <li key={i} className="text-[11px] text-muted-foreground/80 leading-relaxed flex gap-1.5">
                  <span className="text-muted-foreground/40 mt-0.5">•</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 animate-fade-in-up">
          <button onClick={() => navigate("/")}
            className="flex-1 h-11 sm:h-12 rounded-full border border-border/50 bg-secondary/30 hover:bg-secondary/50 text-sm font-medium transition-all active:scale-[0.98]"
            data-testid="back-home-btn">
            Back to Home
          </button>
          <button onClick={() => { localStorage.removeItem("intakeSessionId"); navigate("/intake"); }}
            className="flex-1 h-11 sm:h-12 rounded-full bg-primary hover:bg-primary/90 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-[0_0_20px_-5px_rgba(99,102,241,0.4)] active:scale-[0.98]"
            data-testid="new-intake-btn">
            New Intake <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
